import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  fetchUnreadCount,
  fetchNotifications,
  markAllNotificationsRead,
  markNotificationRead,
} from '../api/notificationsApi'
import type { NotificationType, PaginatedNotificationsResponse } from '../components/notificationsData'

export function useUnreadCount() {
  return useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: fetchUnreadCount,
    refetchOnWindowFocus: true,
  })
}

export function useNotifications(filter?: 'ALL' | 'UNREAD' | 'READ', type?: NotificationType) {
  const queryClient = useQueryClient()
  const query = useQuery<PaginatedNotificationsResponse, Error>({
    queryKey: ['notifications', 'list', filter, type],
    queryFn: () => fetchNotifications({ filter, type }),
  })
  const markReadMutation = useMarkNotificationRead()
  const markAllReadMutation = useMarkAllNotificationsRead()

  const notifications = query.data?.items || []
  const unreadCount = notifications.filter((n: any) => !n.isRead && !n.read).length

  return {
    ...query,
    notifications,
    unreadCount,
    markAsRead: (id: string) => {
      queryClient.setQueryData(['notifications', 'list', filter, type], (old: any) => {
        if (!old) return old
        const items = (old.items || []).map((item: any) => (item.id === id ? { ...item, isRead: true, read: true } : item))
        return { ...old, items }
      })
      markReadMutation.mutate(id)
    },
    markAllAsRead: () => {
      queryClient.setQueryData(['notifications', 'list', filter, type], (old: any) => {
        if (!old) return old
        const items = (old.items || []).map((item: any) => ({ ...item, isRead: true, read: true }))
        return { ...old, items }
      })
      markAllReadMutation.mutate()
    },
    setNotifications: (updater: any) => {
      const next = typeof updater === 'function' ? updater(notifications) : updater
      queryClient.setQueryData(['notifications', 'list', filter, type], {
        items: next,
        meta: query.data?.meta || { page: 1, limit: 10, total: next.length, totalPages: 1, hasNextPage: false, hasPreviousPage: false },
      })
    },
  }
}

export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: markAllNotificationsRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications', 'unread-count'] })
      queryClient.invalidateQueries({ queryKey: ['notifications', 'list'] })
    },
  })
}

export function useMarkNotificationRead() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => markNotificationRead(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications', 'unread-count'] })
      queryClient.invalidateQueries({ queryKey: ['notifications', 'list'] })
    },
  })
}
